'use strict';

const crypto = require('crypto');
const Logger = require('./Logger');

/**
 * Interný tajný prefix (pepper), ktorý sa pridáva pred heslo používateľa.
 * Bez tohto reťazca nie je možné dešifrovať dáta ani so správnym heslom.
 *
 * BEZPEČNOSTNÉ UPOZORNENIE: predvolená hodnota nižšie začína prefixom
 * `REPLACE_ME:` a je súčasťou publikovaného balíka — teda verejne známa
 * každému, kto si balík stiahne. Ak sa nenahradí, pepper neposkytuje žiadnu
 * dodatočnú ochranu a bezpečnostný model sa zredukuje na samotné heslo.
 * Knižnica na nenahradený pepper zámerne NEupozorňuje za behu (žiadny warning,
 * žiadna výnimka), takže kontrola je plne na integrujúcej aplikácii. Overiť sa
 * dá cez {@link CryptoEngine.getPepper} — napríklad `getPepper().startsWith('REPLACE_ME:')`
 * pri štarte aplikácie.
 *
 * Nahradiť sa dá dvoma spôsobmi:
 * - priamo v tomto súbore prepísaním reťazca (platí pre celý build),
 * - programaticky cez {@link CryptoEngine.setPepper} PRED vytvorením inštancie
 *   (odporúčané — pepper môže prísť z ENV alebo z build pipeline).
 *
 * Pepper musí byť identický na strane, ktorá šifruje, aj na strane, ktorá
 * dešifruje. Pri nezhode dešifrovanie zlyhá na overení GCM tagu.
 *
 * @private
 * @type {string}
 */
let _pepper = 'REPLACE_ME:Xk9v2Tm4Qp7Lw1Zr8Bn3Yf6Hj0Sd5Uc4Wg8Ma1Oe3Ri7Vx2Kl9Nq6Dp5Jt0Eb';

/**
 * Mapovanie JSON typov na obfuskované aliasy.
 * Každý typ má viacero aliasov — pri šifrovaní sa náhodne vyberie jeden.
 * Bez prístupu k zdrojovému kódu nie je možné určiť pôvodný typ hodnoty.
 *
 * @private
 * @type {Object<string, string[]>}
 */
const TYPE_TO_ALIASES = {
  string: ['a', 'f', 'k', 'p', 'u', 'C', 'H', 'M', 'R', 'W', '1', '4', '7', 'e', 'J', 'O'],
  number: ['b', 'g', 'l', 'q', 'v', 'D', 'I', 'N', 'S', 'X', '2', '5', '8', 'j', 'K', 'T'],
  boolean: ['c', 'h', 'm', 'r', 'w', 'E', 'L', 'P', 'U', 'Y', '3', '6', '9', 'o', 'Z'],
  null: ['d', 'i', 'n', 's', 'x', 'F', 'G', 'Q', 'V', '0', 'A', 'B', 't', 'y', 'z'],
};

/**
 * Reverzná mapa: alias → typ.
 * Generuje sa automaticky z TYPE_TO_ALIASES.
 *
 * @private
 * @type {Object<string, string>}
 */
const ALIAS_TO_TYPE = {};
for (const [type, aliases] of Object.entries(TYPE_TO_ALIASES)) {
  for (const alias of aliases) {
    ALIAS_TO_TYPE[alias] = type;
  }
}

/**
 * Parametre pre scrypt odvodenie kľúča.
 *
 * @private
 * @type {Object}
 */
const SCRYPT_PARAMS = {
  /**
   * Nákladový parameter (CPU/memory cost parameter) pre scrypt.
   *
   * AKTUÁLNE NASTAVENIE: 2^15 = 32 768 (~32 MB, ~100 ms na odvodenie kľúča).
   *
   * Určuje koľko pamäte a CPU sa spotrebuje pri odvodení kľúča z hesla.
   * Vyššie N = drahší brute-force útok, ale pomalšie šifrovanie/dešifrovanie.
   * Hodnota musí byť mocnina dvojky.
   *
   * Odporúčané hodnoty:
   *   2^14 (16 384)    ~16 MB   ~50ms    Bežné aplikácie, časté šifrovanie za behu
   *   2^15 (32 768)    ~32 MB   ~100ms   Štandardná bezpečnosť (aktuálne nastavenie)
   *   2^17 (131 072)   ~128 MB  ~400ms   Citlivé dáta
   *   2^20 (1 048 576) ~1 GB    ~3s      Maximálna odolnosť, len pre offline operácie
   *
   * Pre konfiguračné súbory šifrované cez CLI a dešifrované raz pri štarte
   * servera je vyššie N ideálne — spomalenie je jednorazové a zanedbateľné,
   * ale útočníkovi exponenciálne zdražuje každý pokus o uhádnutie hesla.
   *
   * POZOR: zmena N je rozbíjajúca zmena formátu. Hodnota sa neukladá do
   * zašifrovaného payloadu, takže dáta zašifrované pri jednom N sa už nedajú
   * dešifrovať po jeho zmene — odvodí sa iný kľúč a GCM tag neprejde overením.
   * Pri zvyšovaní N je nutné všetky existujúce súbory najprv dešifrovať starou
   * verziou a znovu zašifrovať novou.
   *
   * Pamäť sa počíta: 128 * N * r bytov (pri r=8 a N=2^15 → ~32 MB).
   */
  N: 2 ** 15,
  /** Veľkosť bloku */
  r: 8,
  /** Paralelizačný faktor */
  p: 1,
  /** Dĺžka odvodeného kľúča v bytoch (256 bitov) */
  keyLength: 32,
};

/** Dĺžka inicializačného vektora v bytoch */
const IV_LENGTH = 16;

/** Dĺžka salt-u v bytoch */
const SALT_LENGTH = 32;

/** Dĺžka GCM autentifikačného tagu v bytoch */
const AUTH_TAG_LENGTH = 16;

/**
 * @typedef {Object} EncryptedPayload
 * @property {true} $enc - Marker identifikujúci zašifrovanú hodnotu
 * @property {string} t - Obfuskovaný alias typu pôvodnej hodnoty
 * @property {string} d - Zašifrované dáta (base64)
 * @property {string} v - Inicializačný vektor (base64)
 * @property {string} s - Salt pre scrypt (base64)
 * @property {string} g - GCM autentifikačný tag (base64)
 */

/**
 * Nízkoúrovňový šifrovací engine využívajúci AES-256-GCM so scrypt KDF a pepper ochranou.
 *
 * Trieda poskytuje základné operácie šifrovania a dešifrovania jednotlivých hodnôt.
 * Pre vysokoúrovňové operácie s celými JSON objektmi použite {@link ConfigCrypto}.
 *
 * @class
 */
class CryptoEngine {
  /**
   * Vytvorí novú inštanciu šifrovacieho engine.
   *
   * @param {Object} options - Konfigurácia
   * @param {string} options.password - Heslo používateľa pre šifrovanie/dešifrovanie
   * @throws {Error} Ak heslo nie je zadané alebo je prázdne
   */
  constructor({ password }) {
    if (!password || typeof password !== 'string') {
      throw new Error('Heslo je povinné a musí byť neprázdny reťazec.');
    }

    /**
     * Kombinácia pepperu a hesla používateľa.
     * @private
     * @type {string}
     */
    this._compositePassword = _pepper + password;
    Logger.verbose('[CryptoEngine]', 'Inštancia vytvorená.');
  }

  /**
   * Odvodí šifrovací kľúč z hesla pomocou scrypt.
   *
   * @private
   * @param {Buffer} salt - Náhodný salt
   * @returns {Buffer} Odvodený 256-bitový kľúč
   */
  _deriveKey(salt) {
    Logger.verbose('[CryptoEngine]', `Odvodzujem kľúč cez scrypt (N=${SCRYPT_PARAMS.N}, r=${SCRYPT_PARAMS.r}, p=${SCRYPT_PARAMS.p}).`);
    return crypto.scryptSync(this._compositePassword, salt, SCRYPT_PARAMS.keyLength, {
      N: SCRYPT_PARAMS.N,
      r: SCRYPT_PARAMS.r,
      p: SCRYPT_PARAMS.p,
      maxmem: 128 * SCRYPT_PARAMS.N * SCRYPT_PARAMS.r * 2,
    });
  }

  /**
   * Vráti náhodný alias pre zadaný JSON typ.
   *
   * @private
   * @param {string} typeName - Názov typu ('string' | 'number' | 'boolean' | 'null')
   * @returns {string} Náhodne vybraný alias
   */
  _getRandomAlias(typeName) {
    const aliases = TYPE_TO_ALIASES[typeName];
    const index = crypto.randomInt(aliases.length);
    return aliases[index];
  }

  /**
   * Zistí JSON typ hodnoty pre obfuskáciu.
   *
   * @private
   * @param {*} value - Ľubovoľná JSON hodnota
   * @returns {string} Názov typu ('string' | 'number' | 'boolean' | 'null')
   * @throws {Error} Ak hodnota nie je platný JSON primitív
   */
  _resolveType(value) {
    if (value === null) return 'null';
    const type = typeof value;
    if (type === 'string' || type === 'number' || type === 'boolean') return type;
    throw new Error(`Nepodporovaný typ hodnoty pre šifrovanie: ${type}`);
  }

  /**
   * Zašifruje jednu primitívnu hodnotu.
   *
   * Hodnota sa konvertuje na reťazec, zašifruje pomocou AES-256-GCM
   * s unikátnym IV a salt-om, a zabalí do štandardného formátu.
   *
   * @param {string|number|boolean|null} value - Hodnota na zašifrovanie
   * @returns {EncryptedPayload} Objekt so zašifrovanými dátami a metadátami
   * @throws {Error} Ak hodnota nie je platný JSON primitív
   */
  encrypt(value) {
    const typeName = this._resolveType(value);
    const plaintext = String(value);
    Logger.verbose('[CryptoEngine]', `Šifrujem hodnotu (${typeName}, ${plaintext.length} znakov).`);

    const salt = crypto.randomBytes(SALT_LENGTH);
    const iv = crypto.randomBytes(IV_LENGTH);
    const key = this._deriveKey(salt);

    const cipher = crypto.createCipheriv('aes-256-gcm', key, iv, {
      authTagLength: AUTH_TAG_LENGTH,
    });

    const encrypted = Buffer.concat([
      cipher.update(plaintext, 'utf8'),
      cipher.final(),
    ]);

    const tag = cipher.getAuthTag();

    return {
      $enc: true,
      t: this._getRandomAlias(typeName),
      d: encrypted.toString('base64'),
      v: iv.toString('base64'),
      s: salt.toString('base64'),
      g: tag.toString('base64'),
    };
  }

  /**
   * Dešifruje jednu zašifrovanú hodnotu.
   *
   * Overí GCM autentifikačný tag (integritu dát) a vráti hodnotu
   * v pôvodnom type (string, number, boolean alebo null).
   *
   * @param {EncryptedPayload} payload - Objekt so zašifrovanými dátami
   * @returns {string|number|boolean|null} Pôvodná hodnota v správnom type
   * @throws {Error} Ak dáta boli poškodené alebo je heslo nesprávne
   */
  decrypt(payload) {
    const {
      t,
      d,
      v,
      s,
      g,
    } = payload;

    const typeName = ALIAS_TO_TYPE[t];
    if (!typeName) {
      throw new Error(`Neznámy alias typu: "${t}". Dáta môžu byť poškodené.`);
    }
    Logger.verbose('[CryptoEngine]', `Dešifrujem hodnotu (alias="${t}" → ${typeName}).`);

    const salt = Buffer.from(s, 'base64');
    const iv = Buffer.from(v, 'base64');
    const encrypted = Buffer.from(d, 'base64');
    const tag = Buffer.from(g, 'base64');
    const key = this._deriveKey(salt);

    const decipher = crypto.createDecipheriv('aes-256-gcm', key, iv, {
      authTagLength: AUTH_TAG_LENGTH,
    });
    decipher.setAuthTag(tag);

    let decrypted;
    try {
      decrypted = decipher.update(encrypted, undefined, 'utf8') + decipher.final('utf8');
    } catch (/** @type {*} */ err) {
      throw new Error(
        'Dešifrovanie zlyhalo — nesprávne heslo alebo poškodené dáta. ' +
        `Pôvodná chyba: ${err.message}`,
      );
    }

    return CryptoEngine._castToType(decrypted, typeName);
  }

  /**
   * Konvertuje dešifrovaný reťazec späť na pôvodný JSON typ.
   *
   * @private
   * @static
   * @param {string} value - Dešifrovaný reťazec
   * @param {string} typeName - Názov cieľového typu
   * @returns {string|number|boolean|null} Hodnota v správnom type
   */
  static _castToType(value, typeName) {
    switch (typeName) {
      case 'string':
        return value;
      case 'number': {
        const num = Number(value);
        if (Number.isNaN(num)) {
          throw new Error(`Nedá sa previesť "${value}" na číslo.`);
        }
        return num;
      }
      case 'boolean':
        return value === 'true';
      case 'null':
        return null;
      default:
        throw new Error(`Neznámy typ: "${typeName}".`);
    }
  }

  /**
   * Overí, či je objekt zašifrovaný payload ($enc marker).
   *
   * @static
   * @param {*} value - Hodnota na overenie
   * @returns {boolean} True ak je to zašifrovaný payload
   */
  static isEncryptedPayload(value) {
    return (
      typeof value === 'object' &&
      value !== null &&
      value.$enc === true &&
      typeof value.t === 'string' &&
      typeof value.d === 'string' &&
      typeof value.v === 'string' &&
      typeof value.s === 'string' &&
      typeof value.g === 'string'
    );
  }

  /**
   * Nastaví pepper (tajný prefix) pre všetky budúce inštancie.
   *
   * Volajte PRED vytvorením inštancie CryptoEngine alebo ConfigCrypto.
   * Pepper sa aplikuje až pri konštrukcii novej inštancie — existujúce
   * inštancie si zachovajú pôvodný pepper.
   *
   * @static
   * @param {string} pepper - Tajný reťazec (prefix pre heslo)
   * @throws {Error} Ak pepper nie je reťazec
   *
   * @example
   * const { CryptoEngine } = require('@soitron/json-value-crypto');
   *
   * // Pozor: setPepper vyhodí výnimku, ak je hodnota undefined (nenastavená
   * // ENV premenná). Preto sa oplatí doplniť fallback alebo explicitnú kontrolu.
   * if (!process.env.APP_PEPPER) {
   *   throw new Error('APP_PEPPER nie je nastaveny.')
   * }
   * CryptoEngine.setPepper(process.env.APP_PEPPER);
   */
  static setPepper(pepper) {
    if (typeof pepper !== 'string') {
      throw new Error('Pepper musí byť reťazec.');
    }
    _pepper = pepper;
  }

  /**
   * Vráti aktuálne nastavený pepper.
   *
   * Užitočné pre overenie, že pepper bol nastavený pred štartom aplikácie.
   * Pozor: vracia citlivú hodnotu — nelogujte do konzoly v produkcii.
   *
   * @static
   * @returns {string} Aktuálny pepper
   */
  static getPepper() {
    return _pepper;
  }
}

module.exports = CryptoEngine;
